import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';
import { subscriptionStore } from '@/features/subscription';
import { cn } from '@/lib/utils';

import { teamApis } from '../team.query';
import { teamStore } from '../team.store';
import {
  TierDisplayLabel,
  type BillingInterval,
  type MemberTier,
} from '../team.types';

const FEATURES = [
  'Centralized billing for your company',
  'Invite and manage team members',
  'Mix Plus, Power, Pro, and Pro2 seats in one team',
];

function formatSeatPrice(cents: number | null): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export const NoTeamView = observer(function NoTeamView() {
  const [showForm, setShowForm] = useState(false);
  const [planNotice, setPlanNotice] = useState<string | null>(null);
  const [checkingPlan, setCheckingPlan] = useState(false);
  const [name, setName] = useState('');
  const [interval, setInterval] = useState<BillingInterval>('annual');
  const [tier, setTier] = useState<MemberTier>('plus');

  useEffect(() => {
    if (!teamStore.pricingMap) {
      teamApis.fetchPricing();
    }
  }, []);

  const hasActiveSubscription =
    subscriptionStore.state.subscriptionStatus === 'active' ||
    subscriptionStore.state.subscriptionStatus === 'trialing' ||
    subscriptionStore.state.subscriptionStatus === 'past_due';

  const handleCreateClick = async () => {
    setCheckingPlan(true);
    const credit = await teamApis.fetchIndividualPlanCredit();
    setCheckingPlan(false);
    if (credit?.has_stripe_subscription) {
      setPlanNotice(
        credit.credit_cents > 0
          ? `When your team payment goes through, we'll cancel it and add about $${(credit.credit_cents / 100).toFixed(2)} for its unused time to your team's balance. The balance pays toward your next team invoices.`
          : "It will be cancelled when your team payment goes through. There's no unused paid time to credit."
      );
    } else if (credit && hasActiveSubscription) {
      setPlanNotice(
        'If you subscribed in the App Store or Play Store, cancel it there.'
      );
    } else if (hasActiveSubscription) {
      setPlanNotice(
        "When your team payment goes through, we'll cancel it and add its unused time to your team's balance. If you subscribed in the App Store or Play Store, cancel it there."
      );
    } else {
      setShowForm(true);
    }
  };

  const handleContinueFromNotice = () => {
    setPlanNotice(null);
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    await teamApis.createCheckout({
      name: name.trim(),
      billing_interval: interval,
      owner_tier: tier,
    });
  };

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Team</SettingsSection.Title>
      </SettingsSection.Header>

      {!showForm ? (
        <YStack className="gap-4">
          <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl py-5 px-6 gap-5">
            <YStack className="gap-1">
              <h3 className="font-serif text-md text-foreground">
                Littlebird for Teams
              </h3>
              <p className="text-sm text-foreground-muted leading-relaxed">
                Get Littlebird for your whole company with one subscription.
              </p>
            </YStack>

            <ul className="space-y-2">
              {FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="flex items-center gap-2 text-sm text-foreground-muted"
                >
                  <Icons.check className="w-3.5 h-3.5 text-success-11 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <XStack className="items-center gap-3 pt-3 border-t border-border">
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateClick}
                isLoading={checkingPlan}
              >
                Create a Team
              </Button>
              {teamStore.getPriceCents('plus', 'annual') != null && (
                <span className="text-xs text-foreground-muted">
                  Starting at{' '}
                  {formatSeatPrice(teamStore.getPriceCents('plus', 'annual'))}
                  /seat/mo billed annually
                </span>
              )}
            </XStack>
          </YStack>
        </YStack>
      ) : (
        <YStack className="gap-5 py-2">
          <YStack className="gap-1.5">
            <label className="text-sm font-medium text-foreground">
              Team name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc"
              autoFocus
              className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </YStack>

          <YStack className="gap-2">
            <label className="text-sm font-medium text-foreground">
              Billing interval
            </label>
            <XStack className="gap-2">
              {(['annual', 'monthly'] as const).map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setInterval(i)}
                  className={cn(
                    'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all',
                    interval === i
                      ? 'border-accent bg-accent/5 text-foreground'
                      : 'border-border text-foreground-muted hover:border-border-strongest'
                  )}
                >
                  {i === 'annual' ? 'Annual' : 'Monthly'}
                </button>
              ))}
            </XStack>
          </YStack>

          <YStack className="gap-2">
            <label className="text-sm font-medium text-foreground">
              Your plan tier
            </label>
            <XStack className="gap-2">
              {(['plus', 'power', 'pro', 'pro2'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  className={cn(
                    'flex-1 rounded-lg border p-3 text-left transition-all',
                    'bg-surface hover:border-border-strongest',
                    tier === t
                      ? 'border-accent ring-1 ring-accent'
                      : 'border-border dark:border-border-strong'
                  )}
                >
                  <span className="text-sm font-semibold text-foreground">
                    {TierDisplayLabel[t]}
                  </span>
                  <XStack className="items-baseline gap-0.5 mt-0.5">
                    <span className="text-base font-bold text-foreground">
                      {formatSeatPrice(teamStore.getPriceCents(t, interval))}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      /seat/mo
                    </span>
                  </XStack>
                </button>
              ))}
            </XStack>
            <p className="text-xs text-foreground-muted">
              You can assign different tiers to each team member later.
            </p>
          </YStack>

          {teamStore.error && <ErrorAlert message={teamStore.error} />}

          <XStack className="gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleCreate}
              isLoading={teamStore.checkoutLoading}
              loadingText="Redirecting..."
              disabled={!name.trim()}
            >
              Continue to Payment
            </Button>
          </XStack>
        </YStack>
      )}

      <Dialog.Root
        open={planNotice !== null}
        onOpenChange={(open) => !open && setPlanNotice(null)}
      >
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>You have an individual subscription</Dialog.Title>
            <Dialog.Description>{planNotice}</Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPlanNotice(null)}
            >
              Go Back
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleContinueFromNotice}
            >
              Continue
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </SettingsSection.Root>
  );
});

function ErrorAlert({ message }: { message: string }) {
  return (
    <XStack className="items-center gap-2 rounded-lg bg-danger-3 px-3 py-2">
      <Icons.warnTriangle className="w-3.5 h-3.5 text-danger-11 shrink-0" />
      <span className="text-sm text-danger-11">{message}</span>
    </XStack>
  );
}
